import React, { useState } from 'react';
import { X, FileDown, MessageCircle, Mail, Loader, CheckCircle, Copy, AlertCircle } from 'lucide-react';
import { authHeaders } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ShareReportModal = ({ isOpen, onClose }) => {
  const [phase, setPhase] = useState('idle'); // idle, generating, ready, sending, sent, error
  const [shareToken, setShareToken] = useState(null);
  const [testCount, setTestCount] = useState(0);
  const [emailInput, setEmailInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [copied, setCopied] = useState(false);

  const generateReport = async () => {
    setPhase('generating');
    try {
      const res = await fetch(`${API}/api/reports/generate`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Rapor oluşturulamadı');
      }
      const data = await res.json();
      setShareToken(data.share_token);
      setTestCount(data.test_count);
      setPhase('ready');
    } catch (e) {
      setPhase('error');
    }
  };

  const downloadPdf = async () => {
    if (!shareToken) return;
    try {
      const res = await fetch(`${API}/api/reports/${shareToken}/pdf`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Doktorun_Cebinde_Rapor_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('PDF indirilemedi');
    }
  };

  const shareWhatsApp = async () => {
    if (!shareToken) return;
    try {
      const res = await fetch(`${API}/api/reports/${shareToken}/whatsapp`, { headers: authHeaders() });
      const data = await res.json();
      window.open(data.whatsapp_url, '_blank');
    } catch {
      alert('WhatsApp paylaşımı başarısız');
    }
  };

  const sendEmail = async () => {
    if (!shareToken || !emailInput) return;
    setEmailSending(true);
    setEmailError('');
    try {
      const res = await fetch(`${API}/api/reports/${shareToken}/email`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ recipient_email: emailInput, sender_note: noteInput }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'E-posta gönderilemedi');
      }
      setEmailSent(true);
    } catch (e) {
      setEmailError(e.message);
    } finally {
      setEmailSending(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/report/${shareToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-report-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#1e293b] border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(0,0,0,0.4)' }}>
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition" data-testid="share-modal-close">
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-white mb-1">Rapor Paylaş</h2>
        <p className="text-white/50 text-xs mb-6">Sağlık değerlendirme raporunu paylaş veya indir.</p>

        {/* Phase: Idle — Generate button */}
        {phase === 'idle' && (
          <button onClick={generateReport}
            className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition"
            data-testid="generate-report-btn">
            Rapor Oluştur
          </button>
        )}

        {/* Phase: Generating */}
        {phase === 'generating' && (
          <div className="flex items-center justify-center gap-3 py-8">
            <Loader className="w-6 h-6 text-cyan-400 animate-spin" />
            <span className="text-white/60 text-sm">Rapor hazırlanıyor...</span>
          </div>
        )}

        {/* Phase: Error */}
        {phase === 'error' && (
          <div className="text-center py-6">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-white/60 text-sm mb-4">Rapor oluşturulamadı. Önce bazı testleri tamamlayın.</p>
            <button onClick={onClose} className="px-6 py-2 bg-white/10 text-white/70 rounded-lg text-sm hover:bg-white/15 transition">
              Kapat
            </button>
          </div>
        )}

        {/* Phase: Ready — Share options */}
        {phase === 'ready' && shareToken && (
          <div className="space-y-4">
            {/* Stats */}
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-3" data-testid="report-ready-badge">
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
              <div>
                <p className="text-emerald-300 text-sm font-medium">Rapor hazir!</p>
                <p className="text-white/40 text-xs">{testCount} test sonucu dahil edildi</p>
              </div>
            </div>

            {/* PDF Download */}
            <button onClick={downloadPdf}
              className="w-full flex items-center gap-3 py-3 px-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition"
              data-testid="download-pdf-btn">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center shrink-0">
                <FileDown className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-medium">PDF Indir</p>
                <p className="text-white/40 text-xs">Raporu PDF olarak kaydet</p>
              </div>
            </button>

            {/* WhatsApp */}
            <button onClick={shareWhatsApp}
              className="w-full flex items-center gap-3 py-3 px-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition"
              data-testid="share-whatsapp-btn">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-medium">WhatsApp ile Paylas</p>
                <p className="text-white/40 text-xs">Rapor linkini WhatsApp'tan gonder</p>
              </div>
            </button>

            {/* Copy Link */}
            <button onClick={copyLink}
              className="w-full flex items-center gap-3 py-3 px-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition"
              data-testid="copy-link-btn">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                <Copy className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <p className="text-white text-sm font-medium">{copied ? 'Link Kopyalandi!' : 'Linki Kopyala'}</p>
                <p className="text-white/40 text-xs">Rapor linkini kopyala ve istedigin yerde paylas</p>
              </div>
            </button>

            {/* Email Section */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-testid="email-section">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-cyan-400" />
                <p className="text-white text-sm font-medium">E-posta ile Gonder</p>
              </div>
              {emailSent ? (
                <div className="flex items-center gap-2 py-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-300 text-sm">E-posta gonderildi!</span>
                </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    placeholder="ornek@email.com"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 mb-2"
                    data-testid="email-input"
                  />
                  <input
                    type="text"
                    value={noteInput}
                    onChange={e => setNoteInput(e.target.value)}
                    placeholder="Kisa bir not ekle (opsiyonel)"
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 mb-3"
                    data-testid="note-input"
                  />
                  {emailError && <p className="text-red-400 text-xs mb-2">{emailError}</p>}
                  <button onClick={sendEmail}
                    disabled={!emailInput || emailSending}
                    className={`w-full py-2.5 rounded-lg font-medium text-sm transition ${emailInput && !emailSending ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}
                    data-testid="send-email-btn">
                    {emailSending ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader className="w-4 h-4 animate-spin" /> Gonderiliyor...
                      </span>
                    ) : 'Raporu Gönder'}
                  </button>
                </>
              )}
            </div>

            <p className="text-white/30 text-[10px] text-center">
              Paylaşılan rapor linkini görebilmek için alıcının Doktor Cebinde'ye üye olması gerekmektedir.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareReportModal;
